/**
 * Unified API clients
 * 
 * This module provides exactly two axios clients:
 * - apiClient: For main API (VITE_API_BASE_URL)
 * - chatClient: For chat service (VITE_CHAT_SERVICE_URL)
 * 
 * Constitution Section 19.3: Environment Variables & Configuration
 * Constitution Section 19.6: TypeScript Strictness (unknown + narrowing)
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, CHAT_SERVICE_URL, WIZARD_SERVICE_URL } from '@/config/env';
import { logger } from '@/lib/logger';
import { ApiError, classifyError, isApiError } from '@/api/errors';

/**
 * Type-safe error handling: narrow unknown to AxiosError
 */
export function isAxiosError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error);
}

// Re-export the classified error API so callers don't need a second import.
export { ApiError, classifyError, isApiError };

/**
 * Extract error message from unknown error
 */
export function getErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    // Timeout: request took too long (e.g. PDF generation, heavy endpoints)
    if (error.code === 'ECONNABORTED') {
      return 'La solicitud tardó demasiado. Intenta de nuevo.';
    }

    // AxiosError narrowing
    if (error.response?.data) {
      const data = error.response.data;
      if (typeof data === 'object' && data !== null) {
        if ('detail' in data && typeof data.detail === 'string') {
          return data.detail;
        }
        if ('message' in data && typeof data.message === 'string') {
          return data.message;
        }
      }
    }

    // HTTP status-based messages
    const status = error.response?.status;
    if (status === 401) {
      return 'No tienes autorización para realizar esta acción.';
    }
    if (status === 403) {
      return 'Acceso denegado.';
    }
    if (status === 404) {
      return 'El recurso solicitado no fue encontrado.';
    }
    if (status === 422) {
      // Try to extract detailed validation errors from FastAPI response
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // FastAPI validation errors format: [{loc: ['field'], msg: 'message'}, ...]
          const errors = detail
            .filter((err: any) => err.loc && err.msg)
            .map((err: any) => `${err.loc.join('.')}: ${err.msg}`)
            .join(', ');
          return errors || 'Los datos proporcionados no son válidos.';
        }
        if (typeof detail === 'string') {
          return detail;
        }
      }
      return 'Los datos proporcionados no son válidos.';
    }
    if (status === 500) {
      return 'Error interno del servidor.';
    }
    if (status) {
      return `Error del servidor (${status}).`;
    }

    // Network error (no response: connection refused, CORS, DNS, etc.)
    if (error.request) {
      return 'No se pudo conectar con el servidor. Verifica que el servicio esté en ejecución y tu conexión.';
    }
  }
  
  // Generic error
  if (error instanceof Error) {
    return error.message;
  }
  
  return 'Ocurrió un error inesperado.';
}

/**
 * Get authentication token from storage or Auth0
 * This function will be set by TokenConfigProvider
 */
let getTokenFn: (() => Promise<string | null>) | null = null;

/**
 * Set the token getter function (called by TokenConfigProvider)
 */
export function setTokenGetter(fn: () => Promise<string | null>): void {
  getTokenFn = fn;
}

/**
 * Check if the token getter has been configured by TokenConfigProvider.
 * Used to prevent API calls before auth is ready (avoids 401 race condition).
 */
export function isTokenReady(): boolean {
  return getTokenFn !== null;
}

/**
 * Get access token via the configured getter.
 * Used by endpoints that need raw fetch (e.g. SSE streaming).
 */
export async function getAccessToken(): Promise<string | null> {
  if (!getTokenFn) return null;
  return getTokenFn();
}

/**
 * Get login redirect function (called by TokenConfigProvider)
 */
let loginRedirectFn: (() => void) | null = null;

/**
 * Set the login redirect function (called by TokenConfigProvider)
 */
export function setLoginRedirect(fn: () => void): void {
  loginRedirectFn = fn;
}

/**
 * Imperatively trigger the reauth flow. Used by `useApiError` when an
 * unauthorized event arrives outside the axios interceptor (e.g. SSE).
 * No-op when no login redirect has been wired yet.
 */
export function triggerAuthRedirect(): void {
  if (loginRedirectFn) {
    setTimeout(() => {
      loginRedirectFn!();
    }, 100);
  }
}

/**
 * Request interceptor: Add authentication token, adjust timeout, log FINAL URL
 */
async function addAuthHeader(config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
  // Card CW31sjZO — trazas por request: `logger.debug` (apagado en producción,
  // nivel `error`). Con `console.log` salían en prod y Sentry las guardaba como
  // breadcrumb con la URL entera, incluido el `session_id` de `/sessions/{id}/`.
  logger.debug('[API-CLIENT] addAuthHeader called for:', config.url);

  // Adjust timeout for POST/PUT/DELETE (create/update can be slow; default 10s is too low)
  const method = config.method?.toUpperCase() || 'GET';
  const t = config.timeout;
  if (method === 'POST' || method === 'PUT' || method === 'DELETE') {
    if (t == null || (typeof t === 'number' && t < 60000)) {
      config.timeout = 60000; // 60s minimum for create/update (PDF etc. can override with higher)
    }
  }
  
  // Compute and log FINAL resolved URL BEFORE adding token
  const baseURL = config.baseURL || '';
  const url = config.url || '';
  
  // Build full URL
  let fullUrl: string;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    fullUrl = url;
  } else {
    const base = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
    const path = url.startsWith('/') ? url : `/${url}`;
    fullUrl = `${base}${path}`;
  }
  
  // Add query params if present
  if (config.params) {
    const params = new URLSearchParams(config.params).toString();
    if (params) {
      fullUrl += (fullUrl.includes('?') ? '&' : '?') + params;
    }
  }
  
  // CRITICAL: Log FINAL resolved URL for every request
  if (import.meta.env.DEV) {
    logger.info('[API-CLIENT] Request:', {
      method,
      baseURL,
      url,
      fullUrl,
      timeout: config.timeout,
    });
    
    // GUARDRAIL: Detect and error if https://localhost is being used
    if (fullUrl.includes('https://localhost')) {
      logger.error('[API-CLIENT] ⚠️  CRITICAL: https://localhost detected!', {
        method,
        fullUrl,
        baseURL,
        url,
        guidance: 'This will cause ERR_SSL_PROTOCOL_ERROR. Check env vars.'
      });
    }
  }
  
  if (!getTokenFn) {
    logger.debug('[API-CLIENT] No token getter configured, proceeding without auth');
    return config;
  }

  // Skip if Authorization header already set
  if (config.headers.Authorization) {
    logger.debug('[API-CLIENT] Authorization header already set, skipping');
    return config;
  }

  try {
    logger.debug('[API-CLIENT] Calling getTokenFn...');
    const token = await getTokenFn();
    logger.debug('[API-CLIENT] Token result:', token ? `obtained (${token.length} chars)` : 'null');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      logger.debug('[API-CLIENT] Authorization header set');
    }
    // When token is null we proceed without Authorization so the request can be sent;
    // chat client uses skipAuthRedirect; 401/403 surface to the caller (chat public path
    // responds 200 today, captura de lead se hace vía LeadBridgeModal + wizard).
  } catch (error) {
    console.error('[API-CLIENT] Error in addAuthHeader:', error);
    throw error;
  }

  return config;
}

/**
 * Response interceptor: Handle errors and detect HTML responses
 */
function handleResponseError(error: unknown): Promise<never> {
  // Enhanced logging for 4xx/5xx errors (dev mode only)
  if (isAxiosError(error) && error.response) {
    const status = error.response.status;
    const isDev = import.meta.env.DEV;
    
    if (isDev && (status >= 400 && status < 600)) {
      const errorDetails = {
        status,
        url: error.config?.url || error.request?.responseURL || 'unknown',
        method: error.config?.method?.toUpperCase() || 'unknown',
        requestPayload: error.config?.data ? (typeof error.config.data === 'string' ? JSON.parse(error.config.data) : error.config.data) : null,
        responseData: error.response.data,
        responseHeaders: error.response.headers,
      };
      
      console.error('[API-CLIENT] HTTP Error Details:', errorDetails);
      
      // Special handling for 422 validation errors - show full detail
      if (status === 422 && error.response.data?.detail) {
        console.error('[API-CLIENT] Validation Error Detail:', JSON.stringify(error.response.data.detail, null, 2));
      }
    }
  }

  console.error('[API-CLIENT] handleResponseError called:', {
    isAxiosError: isAxiosError(error),
    hasResponse: isAxiosError(error) ? !!error.response : false,
    hasRequest: isAxiosError(error) ? !!error.request : false,
    status: isAxiosError(error) ? error.response?.status : undefined,
    message: error instanceof Error ? error.message : String(error),
  });

  // Detect HTML responses (wrong base URL)
  if (isAxiosError(error) && error.response) {
    const contentType = error.response.headers?.['content-type'] ?? '';
    if (contentType.includes('text/html')) {
      logger.error('[API] Received HTML instead of JSON. Check base URL configuration.');
      return Promise.reject(
        new ApiError({
          kind: 'unknown',
          status: error.response.status,
          message: 'Wrong API base URL or proxy misconfigured',
          cause: error,
        }),
      );
    }

    // Chat requests with skipAuthRedirect: classify but do NOT redirect.
    const skipAuthRedirect = (error.config as InternalAxiosRequestConfig & {
      skipAuthRedirect?: boolean;
    })?.skipAuthRedirect;
    if (skipAuthRedirect && (error.response.status === 401 || error.response.status === 403)) {
      return Promise.reject(classifyError(error));
    }

    // 401 → redirect to login (main API only). Still return a normalized
    // ApiError so callers see the same shape everywhere.
    if (error.response.status === 401) {
      triggerAuthRedirect();
      const apiErr = classifyError(error);
      // Backward compat shim for existing callers that read these flags.
      (apiErr as ApiError & { isAuthError?: boolean }).isAuthError = true;
      return Promise.reject(apiErr);
    }
    // 403 Forbidden: do NOT redirect, classify as `forbidden`.
    if (error.response.status === 403) {
      const apiErr = classifyError(error);
      (apiErr as ApiError & { isForbidden?: boolean }).isForbidden = true;
      return Promise.reject(apiErr);
    }
  }

  // All other paths → normalized ApiError. Pass through if already classified.
  if (isApiError(error)) {
    return Promise.reject(error);
  }
  return Promise.reject(classifyError(error));
}

/**
 * Main API client for projects-service
 * Base URL: VITE_API_BASE_URL
 * 
 * CONSTITUTION RULE 3: Single Axios factory
 * - This is the CANONICAL HTTP client
 * - All API modules MUST use this client
 * - Timeout: 10s default, adjusted to 60s for POST/PUT/DELETE in interceptor
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // 10 seconds default (adjusted to 60s for POST/PUT/DELETE)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for auth
apiClient.interceptors.request.use(addAuthHeader, (error) => {
  logger.error('[API] Request interceptor error:', error);
  return Promise.reject(error);
});

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  handleResponseError
);

/**
 * Chat service client
 * Base URL: VITE_CHAT_SERVICE_URL
 */
export const chatClient: AxiosInstance = axios.create({
  baseURL: CHAT_SERVICE_URL,
  timeout: 30000, // 30 seconds (LLM calls take longer)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request: add auth when token exists; mark chat requests so 401/403 don't redirect
chatClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const out = await addAuthHeader(config);
    (out as InternalAxiosRequestConfig & { skipAuthRedirect?: boolean }).skipAuthRedirect = true;
    return out;
  },
  (error) => {
    logger.error('[CHAT] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

/**
 * `ApiError.code` del 2xx que llegó con HTML en vez de JSON (proxy o base URL
 * mal configurados). Card CW31sjZO (iteración #3): antes era un `Error` plano,
 * indistinguible de cualquier otra falla; así `useChatResults` lo reporta como
 * body ilegible. Mismo `message` que antes para los callers que lo leen.
 */
export const HTML_RESPONSE_ERROR_CODE = 'html_response';

// Add response interceptor for error handling
chatClient.interceptors.response.use(
  (response) => {
    const contentType = response.headers?.['content-type'] ?? '';
    if (contentType.includes('text/html')) {
      logger.error('[CHAT] Received HTML instead of JSON. Check chat service URL.');
      throw new ApiError({
        kind: 'unknown',
        status: response.status,
        code: HTML_RESPONSE_ERROR_CODE,
        message: 'Wrong chat API base URL or proxy misconfigured',
      });
    }
    return response;
  },
  handleResponseError
);

/**
 * Wizard service client
 * Base URL: VITE_WIZARD_SERVICE_URL (default: http://localhost:8003)
 */
export const wizardClient: AxiosInstance = axios.create({
  baseURL: WIZARD_SERVICE_URL,
  timeout: 30000, // 30 seconds (LLM calls take longer)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request: add auth when token exists; skip auth redirect (wizard handles its own errors)
wizardClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const out = await addAuthHeader(config);
    (out as InternalAxiosRequestConfig & { skipAuthRedirect?: boolean }).skipAuthRedirect = true;
    return out;
  },
  (error) => {
    logger.error('[WIZARD] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
wizardClient.interceptors.response.use(
  (response) => {
    const contentType = response.headers?.['content-type'] ?? '';
    if (contentType.includes('text/html')) {
      logger.error('[WIZARD] Received HTML instead of JSON. Check wizard service URL.');
      throw new Error('Wrong wizard API base URL or proxy misconfigured');
    }
    return response;
  },
  handleResponseError
);

